import React from 'react';
import { FileText, MapPin, Play, ShieldCheck, CheckCircle2, Headphones, Sparkles, ArrowRight } from 'lucide-react';

export const ProcessTimeline: React.FC = () => {
  const steps = [
    { title: 'Requirement', desc: 'Understanding brief, DTD schemas, & sample chapters.', icon: FileText, color: 'from-blue-500 to-blue-600' },
    { title: 'Planning', desc: 'Resource pod allocation, template setup, & timeline mapping.', icon: MapPin, color: 'from-indigo-500 to-indigo-600' },
    { title: 'Execution', desc: 'AI-assisted typesetting, XML tagging, & editorial review.', icon: Play, color: 'from-purple-500 to-purple-600' },
    { title: 'Quality', desc: 'Multi-stage proofing, ePubCheck, & PDF/UA accessibility validation.', icon: ShieldCheck, color: 'from-emerald-500 to-emerald-600' },
    { title: 'Delivery', desc: 'Secure asset package transmission (INDD, PDF/X, EPUB, XML).', icon: CheckCircle2, color: 'from-sky-500 to-sky-600' },
    { title: 'Support', desc: 'Post-delivery revisions, archival storage, & dedicated account support.', icon: Headphones, color: 'from-blue-600 to-purple-600' },
  ];

  return (
    <section className="py-16 sm:py-24 bg-white relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-50 border border-blue-200 text-blue-800 text-xs font-bold uppercase tracking-wider">
            <Sparkles className="w-3.5 h-3.5 text-blue-600" /> Seamless Engagement
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
            Our Engagement Process
          </h2>
          <p className="text-slate-600 text-base sm:text-lg">
            A clear, dependable project roadmap from initial inquiry to long-term post-delivery support.
          </p>
        </div>

        {/* Timeline Grid / Path */}
        <div className="mt-14 relative">
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6 relative z-10">
            {steps.map((step, idx) => {
              const IconComp = step.icon;
              return (
                <div
                  key={idx}
                  className="bg-slate-50 rounded-2xl p-6 border border-slate-200/90 hover:bg-white hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col justify-between group"
                >
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <div className={`w-10 h-10 rounded-xl bg-gradient-to-r ${step.color} text-white flex items-center justify-center shadow-md`}>
                        <IconComp className="w-5 h-5" />
                      </div>
                      <span className="text-xs font-extrabold text-slate-700 bg-white border border-slate-200 px-2 py-0.5 rounded-md shadow-2xs font-mono">
                        0{idx + 1}
                      </span>
                    </div>

                    <h3 className="text-lg font-bold text-slate-900 group-hover:text-blue-700 transition-colors">
                      {step.title}
                    </h3>
                    <p className="text-xs text-slate-600 mt-2 leading-relaxed">
                      {step.desc}
                    </p>
                  </div>

                  <div className="mt-4 pt-3 border-t border-slate-200/60 flex items-center gap-1 text-[11px] font-bold text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span>Phase 0{idx + 1}</span>
                    <ArrowRight className="w-3 h-3" />
                  </div>
                </div>
              );
            })}
          </div>

        </div>

      </div>
    </section>
  );
};
